import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { motion, AnimatePresence } from "framer-motion";
import { withStyles } from "@material-ui/core/styles";

// MUI Components
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Switch from "@material-ui/core/Switch";
import Snackbar from "@material-ui/core/Snackbar";
import Slide from "@material-ui/core/Slide";

// Actions
import {
  fetchPing,
  receivePing,
  createSample,
  fetchConnectDevice,
  requestButtonPress,
  receiveAcquisitionComplete,
  receiveSampleData,
  setSampleDataAvailable,
  setSampleDataSaved,
  updateSamplesSavedFlag,
  errorResponse,
  initiateAutoAdvance,
  deviceConnected,
  changeAcqType,
  requestPicture,
  receiveHealth,
  removeSample,
  toggleAutoAdvance,
  setAutoAdvaceDialogState,
  selectLocation,
  setCurrentAcqType,
  fetchCloseDevice,
  setAutoAdvanceSnackbarState,
  toggleDiagnosticsDialog,
  fetchUploadRawSampleData,
} from "../../actions";

// Icons
import GraphicEqIcon from "@material-ui/icons/GraphicEq";
import Typography from "@material-ui/core/Typography";
import {
  AcqShiftIcon,
  TargetIcon,
  ThicknessIcon,
} from "../../constants/customIcons";
import PublishIcon from "@material-ui/icons/Publish";

// Calibration Highchart
import CalibrationPlot from "./CalibrationPlot";
import { colors } from "../../constants/colors";
import CameraModal from "./CameraModal";
import DiagnosticsDialog from "./DiagnosticsDialog";
import AutoAdvanceDialog from "./AutoAdvanceDialog";
import { customTheme } from "../../constants/customTheme";

const styles = {
  root: {
    boxShadow: (props) =>
      props.theme === "light"
        ? `0px 20px 20px 12px ${colors[props.theme].boxShadowColor},0.75)`
        : `0px 20px 14px 12px ${colors[props.theme].boxShadowColor},0.75)`,
    margin: 0,
    display: "flex",
    flex: "0 0 auto",
    alignItems: "stretch",
    flexWrap: "nowrap",
    position: "sticky",
    backgroundColor: (props) => colors[props.theme].background,
  },
  acquireBtn: {
    padding: "0px",
    marginTop: "3px",
    borderRadius: "50%",
    minWidth: "0px",
  },
  shiftBtn: {
    padding: "0px",
    width: "30%",
    borderRadius: "50px",
  },
  shiftBtnTitle: {
    display: "flex",
    alignItems: "center",
    color: (props) =>
      props.acquireButtonDisable
        ? colors.disabledColor
        : props.currentAcqType === "CSI"
        ? colors[props.theme].primary
        : colors[props.theme].secondary,
  },
  pingBtn: {
    padding: "10px",
    marginRight: "10px",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.02)",
  },
  uploadBtn: {
    padding: "10px",
    backgroundColor: "rgba(255, 255, 255, 0.02)",
  },
  acqShiftIcon: {
    ...customTheme().iconMedium,
    fill: (props) =>
      props.acquireButtonDisable
        ? colors.disabledColor
        : props.currentAcqType === "CSI"
        ? colors[props.theme].primary
        : colors[props.theme].secondary,
  },
  acqIcon: {
    fontSize: "3.0rem",
    fill: (props) =>
      props.acquireButtonDisable
        ? colors.disabledColor
        : props.currentAcqType === "CSI"
        ? colors[props.theme].primary
        : colors[props.theme].secondary,
  },
  deviceListItem: {
    "&.MuiListItem-button:hover": {
      backgroundColor: (props) => colors[props.theme].tableHover,
    },
  },
  snackbar: {
    "& div": {
      backgroundColor: (props) => colors[props.theme].primary, // Color set when snackbar opens
    },
  },
  snackbarMessage: {
    display: "flex",
    alignItems: "center",
    color: (props) => colors[props.theme].snackbarText,
    "& svg": {
      marginRight: "10px",
      color: (props) => colors[props.theme].snackbarText,
    },
  },

  gridSectionOne: {
    width: "25%",
    justifyContent: "space-evenly",
  },
  gridSectionTwo: {
    width: "30%",
    height: "100%",
    alignItems: "center",
    justifyContent: "space-evenly",
    flexDirection: "row",
  },
  gridSectionThree: {
    width: "20%",
    justifyContent: "space-evenly",
  },
  gridSectionFour: {
    alignItems: "center",
    width: "25%",
  },
  AcqModeToggleDiv: {
    alignContent: "center",
    textAlign: "center",
  },
  AcqModeToggleOffLabel: {
    opacity: (props) => (props.device.connectionEstablished ? 0.3 : 1),
  },
  AcqModeToggleOnLabel: {
    opacity: (props) => (props.device.connectionEstablished ? 1 : 0.3),
  },
  AutoAdvanceToggleDiv: {
    alignContent: "center",
    textAlign: "center",
    color: (props) =>
      props.device.connectionEstablished ? "unset" : colors.disabledColor,
  },
  AutoAdvanceToggleOffLabel: {
    opacity: (props) =>
      props.device.connectionEstablished && !props.autoAdvance ? 1 : 0.3,
  },
  AutoAdvanceToggleOnLabel: {
    opacity: (props) =>
      props.device.connectionEstablished && props.autoAdvance ? 1 : 0.3,
  },
  acqTypeIcon: {
    ...customTheme().icon,
    ...customTheme().iconMedium,
  },
};

let countdownTimer;

class AcquireBar extends React.Component {
  constructor(props) {
    super(props);
    this.totalSeconds = 5;

    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
    this.state = {
      acqType: "CSI",
      seconds: this.totalSeconds,
      windowHeight: null,
    };
  }

  // Handle SSE Event for device related events
  // handleEvent(e) {
  //   switch (e.type) {
  //     case "device_connection": // Receive device connection state
  //       console.log("device_connection", JSON.parse(e.data));
  //       this.props.deviceConnected(JSON.parse(e.data));
  //       break;
  //     case "device_health": //Receive device health check info
  //       console.log("device_health", JSON.parse(e.data));
  //       this.props.receiveHealth(JSON.parse(e.data));

  //       break;
  //     case "aline": //Receive a-line data
  //       console.log("aline", JSON.parse(e.data));
  //       this.props.receivePing(JSON.parse(e.data));
  //       break;
  //     case "button_press":
  //       if (
  //         this.props.device.connectionEstablished &&
  //         !this.props.dialogFlag &&
  //         !this.props.cameraModalStatus
  //       ) {
  //         let data = JSON.parse(e.data);
  //         console.log("button_press", data);
  //         if (data.button === "ACQ") {
  //           // Step 1 - create sample and go in "acquiring" state
  //           // if study is locked then new acquisitions shouldnt be allowed
  //           this.props.createSample(data.uuid, data.sample_type); // create a sample
  //         }
  //         // } else if (data.button === "UP") {
  //         //   this.props.nextPosition("UP");
  //         // } else if (data.button === "DOWN") {
  //         //   this.props.nextPosition("DOWN");
  //         // } else if (
  //         //   data.button === "LEFT" &&
  //         //   !this.props.showAutoAdvancePopup
  //         // ) {
  //         //   this.props.nextPosition("LEFT");
  //         // } else if (
  //         //   data.button === "LEFT" &&
  //         //   this.props.showAutoAdvancePopup
  //         // ) {
  //         //   this.handleAutoAdvanceDismiss();
  //         // } else if (
  //         //   data.button === "RIGHT" &&
  //         //   !this.props.showAutoAdvancePopup
  //         // ) {
  //         //   this.props.nextPosition("RIGHT");
  //         // } else if (
  //         //   data.button === "RIGHT" &&
  //         //   this.props.showAutoAdvancePopup
  //         // ) {
  //         //   this.handleAdvance();
  //         // }
  //       }
  //       break;
  //     case "button_shift_press":
  //       console.log("button_shift_press", JSON.parse(e.data));
  //       this.props.changeAcqType(JSON.parse(e.data)["acq_type"]);
  //       break;
  //     case "acq_complete": // Step 2 - exit "acquiring" state and receive raw data
  //       let data = JSON.parse(e.data);
  //       // if (!this.props.webCamState) {
  //       console.log("acq_complete", data);
  //       this.props.receiveAcquisitionComplete(data); // increase SamplesAcquired, calculateStudyProgress
  //       if (data["type"] === "PICTURE") {
  //         this.props.requestPicture(data["uuid"]);
  //       }
  //       // }
  //       break;
  //     case "processed_sample_data": // Step 3 - receive processed data part 1 of 2
  //       console.log("processed_sample_data", JSON.parse(e.data));
  //       var currentdate = new Date();
  //       console.log(
  //         "processed_sample_data",
  //         currentdate.getHours() +
  //           ":" +
  //           currentdate.getMinutes() +
  //           ":" +
  //           currentdate.getSeconds()
  //       );
  //       this.props.receiveSampleData(JSON.parse(e.data));
  //       break;
  //     case "data_available": // Step 5 - data is available at Server Module. Enable result editing (dragging depth)
  //       console.log("data_available", JSON.parse(e.data));
  //       this.props.setSampleDataAvailable(JSON.parse(e.data)["uuid"], true);
  //       break;
  //     case "data_saved": // Step 5 - data is available at Server Module. Enable result editing (dragging depth)
  //       console.log("data_saved", JSON.parse(e.data));
  //       this.props.setSampleDataSaved(JSON.parse(e.data)["uuid"], true);
  //       this.props.updateSamplesSavedFlag();
  //       break;
  //     case "acquisition_error":
  //       this.props.errorResponse(JSON.parse(e.data));
  //       this.props.removeSample(JSON.parse(e.data), "ACQ");
  //       break;
  //     case "processed_sample_data_error":
  //       this.props.errorResponse(JSON.parse(e.data));
  //       this.props.removeSample(JSON.parse(e.data), "VIEW");
  //       break;
  //     case "keydown":
  //       if (
  //         this.props.device.connectionEstablished &&
  //         !this.props.acquiring &&
  //         !this.props.dialogFlag &&
  //         !this.props.cameraModalStatus
  //       ) {
  //         //activate keypress if the device is connected, and no acquisition, and no dialog is open
  //         if (e.keyCode === 32) {
  //           // "SPACEBAR" key => take acquisition
  //           e.preventDefault();
  //           this.handleAcquire();
  //         } else if (e.keyCode === 39) {
  //           // right arrow
  //           e.preventDefault();
  //           this.props.nextPosition("RIGHT");
  //         } else if (e.keyCode === 37) {
  //           // left arrow
  //           e.preventDefault();
  //           this.props.nextPosition("LEFT");
  //         } else if (e.keyCode === 38) {
  //           // up arrow
  //           e.preventDefault();
  //           this.props.nextPosition("UP");
  //         } else if (e.keyCode === 40) {
  //           // down arrow (40 is down arrow)
  //           e.preventDefault();
  //           this.props.nextPosition("DOWN");
  //         }
  //       }
  //       break;
  //     default:
  //   }
  // }

  componentDidMount() {
    // Listening for SSEs
    //Receive confirmation that device is successfully connected
    // this.props.eventSource.addEventListener("device_connection", this, false);

    // Receive a-line data
    // this.props.eventSource.addEventListener("aline", this, false);

    // Step 1 - create sample and go in "acquiring" state
    // this.props.eventSource.addEventListener("button_press", this, false);

    // Acquisition Completed
    // Step 2 - exit "acquiring" state and receive raw data
    // this.props.eventSource.addEventListener("acq_complete", this, false);

    // Receive processed sample data
    // Step 3 - receive processed data part 1 of 2
    // this.props.eventSource.addEventListener(
    //   "processed_sample_data",
    //   this,
    //   false
    // );

    // Step 4 - receive processed data part 2 of 2 (MMODE)
    // this.props.eventSource.addEventListener(
    //   "additional_processed_sample_data",
    //   this,
    //   false
    // );

    // Step 5 - data is available at Server Module. Enable result editing (dragging depth)
    // this.props.eventSource.addEventListener("data_available", this, false);
    // this.props.eventSource.addEventListener("data_saved", this, false);

    // Catch error reports
    // this.props.eventSource.addEventListener("acquisition_error", this, false);
    // this.props.eventSource.addEventListener(
    //   "processed_sample_data_error",
    //   this,
    //   false
    // );

    // this.props.eventSource.addEventListener("device_health", this, false);

    //Acq type change button is pressed
    // this.props.eventSource.addEventListener("button_shift_press", this, false);

    // check if auto-advance should be enabled by default for study
    this.props.initiateAutoAdvance();

    // Keypress eventlistener
    // document.addEventListener("keydown", this, false);

    this.updateWindowDimensions();
    window.addEventListener("resize", this.updateWindowDimensions);
  }

  componentWillUnmount() {
    // this.props.eventSource.removeEventListener(
    //   "device_connection",
    //   this,
    //   false
    // );
    // this.props.eventSource.removeEventListener("aline", this, false);
    // this.props.eventSource.removeEventListener("button_press", this, false);
    // this.props.eventSource.removeEventListener(
    //   "button_shift_press",
    //   this,
    //   false
    // );
    // this.props.eventSource.removeEventListener("acq_complete", this, false);
    // this.props.eventSource.removeEventListener(
    //   "processed_sample_data",
    //   this,
    //   false
    // );
    // this.props.eventSource.removeEventListener(
    //   "acquisition_error",
    //   this,
    //   false
    // );
    // this.props.eventSource.removeEventListener(
    //   "processed_sample_data_error",
    //   this,
    //   false
    // );
    // this.props.eventSource.removeEventListener("device_health", this, false);
    document.removeEventListener("keydown", this, false);
    window.removeEventListener("resize", this.updateWindowDimensions);
  }

  handleAcquire() {
    this.props.requestButtonPress(); // set acquiring = true, simulate button press -> server
  }

  handleAcqTypeChange() {
    if (
      Object.keys(this.props.acqTypes).indexOf(this.props.currentAcqType) ===
      Object.keys(this.props.acqTypes).length - 1
    ) {
      this.props.changeAcqType(Object.keys(this.props.acqTypes)[0]);
    } else {
      this.props.changeAcqType(
        Object.keys(this.props.acqTypes)[
          Object.keys(this.props.acqTypes).indexOf(this.props.currentAcqType) +
            1
        ]
      );
    }
  }

  updateWindowDimensions() {
    this.setState({
      ...this.state,
      windowHeight: window.innerHeight > 500 ? 90 : 70,
    });
  }

  handleAutoAdvanceDismiss() {
    clearInterval(countdownTimer); // clear timer
    this.props.setAutoAdvaceDialogState(false); // close popup
    this.props.toggleAutoAdvance(); // disable autoAdvance
  }

  resetAutoAdvanceTimeout = () => {
    this.setState({
      seconds: this.totalSeconds,
    });
  };

  handleAutoAdvanceTimeout = () => {
    countdownTimer = setInterval(() => {
      if (this.state.seconds > 0) {
        this.setState({
          seconds: this.state.seconds - 1,
        });
      } else {
        // clearInterval(countdownTimer);
        this.handleAdvance();
      }
    }, 1000);
  };

  handleAdvance() {
    clearInterval(countdownTimer); // clear timer
    this.props.setAutoAdvaceDialogState(false); // close popup
    this.props.selectLocation(
      this.props.nextAutoStep.locationId,
      this.props.nextAutoStep.acqType
    ); // advance to next location
    this.props.setCurrentAcqType(this.props.nextAutoStep.acqType);
  }

  handleToggleDeviceActivate(e) {
    if (e.target.checked) {
      this.props.fetchConnectDevice();
      this.props.toggleDiagnosticsDialog();
    } else {
      this.props.fetchCloseDevice();
    }
  }

  handleSampleUpload() {
    // const { dialog } = window.require("electron").remote;
    // dialog.showOpenDialog(
    //   {
    //     properties: ["openFile"],
    //     filters: [
    //       {
    //         name: "Raw Data",
    //         extensions: ["iq.encrypt", "iq"],
    //       },
    //     ],
    //   },
    //   (filePaths) => {
    //     if (filePaths !== undefined && filePaths.length !== 0) {
    //       let sampleUuid = filePaths[0]
    //         .replace(/\\/g, "/")
    //         .split("/")
    //         [filePaths[0].replace(/\\/g, "/").split("/").length - 1].split(
    //           "."
    //         )[0];
    //       if (this.props.allSampleIds.includes(sampleUuid)) {
    //         this.props.errorResponse(
    //           `The sample uuid ${sampleUuid} is already taken`
    //         );
    //       } else {
    //         this.props.fetchUploadRawSampleData(filePaths[0]);
    //       }
    //     }
    //   }
    // );
  }

  render() {
    const { classes } = this.props;

    return (
      <React.Fragment>
        <AnimatePresence>
          {this.props.showAcquireBar && (
            <motion.div
              className={classes.root}
              initial={{ height: 0 }}
              animate={{
                height: this.state.windowHeight, // Shorter acquireBar for smaller screen (e.g. rpi screen)
              }}
              exit={{ height: 0 }}
            >
              <Grid item container alignItems="center" wrap="nowrap">
                {/* SECTION ONE */}
                <Grid container item className={classes.gridSectionOne}>
                  <Typography
                    component="div"
                    className={classes.AcqModeToggleDiv}
                  >
                    Acquisition Mode
                    <Grid
                      component="label"
                      container
                      alignItems="center"
                      spacing={1}
                    >
                      <Grid item className={classes.AcqModeToggleOffLabel}>
                        Off
                      </Grid>
                      <Grid item>
                        <Switch
                          onChange={(e) => this.handleToggleDeviceActivate(e)}
                          checked={this.props.device.connectionEstablished}
                          color="primary"
                        />
                      </Grid>
                      <Grid item className={classes.AcqModeToggleOnLabel}>
                        On
                      </Grid>
                    </Grid>
                  </Typography>
                </Grid>

                {/* SECTION TWO */}
                <Grid item container className={classes.gridSectionTwo}>
                  {/* ACQ TYPE TITLE */}

                  {this.props.device.simulate && (
                    <Tooltip
                      title="Upload Sample Data"
                      enterTouchDelay={300}
                      classes={{
                        tooltip: classes.tooltip,
                      }}
                    >
                      <div>
                        <IconButton
                          disabled
                          onClick={() => this.handleSampleUpload()}
                          className={classes.uploadBtn}
                          // disabled={
                          //   !this.props.device.connectionEstablished ||
                          //   this.props.acquiring
                          // }
                        >
                          <PublishIcon />
                        </IconButton>
                      </div>
                    </Tooltip>
                  )}

                  {!this.props.device.simulate && (
                    <Typography
                      component="div"
                      className={classes.shiftBtnTitle}
                    >
                      {this.props.currentAcqType === "BMODE" ? (
                        <ThicknessIcon className={classes.acqTypeIcon} />
                      ) : this.props.currentAcqType === "CSI" ? (
                        <TargetIcon className={classes.acqTypeIcon} />
                      ) : null}
                      {this.props.acqTypes[this.props.currentAcqType]["name"]}
                    </Typography>
                  )}

                  {/* ACQ SHIFT BUTTON */}
                  <Tooltip
                    title="Change Mode"
                    enterTouchDelay={300}
                    classes={{
                      tooltip: classes.tooltip,
                    }}
                  >
                    <div>
                      <Button
                        variant="outlined"
                        color={
                          this.props.currentAcqType === "CSI"
                            ? "primary"
                            : "secondary"
                        }
                        className={classes.shiftBtn}
                        onClick={() => this.handleAcqTypeChange()}
                        disabled={this.props.acquireButtonDisable}
                      >
                        <AcqShiftIcon className={classes.acqShiftIcon} />
                      </Button>
                    </div>
                  </Tooltip>

                  {this.props.device.simulate && (
                    <Tooltip
                      title="Acquire"
                      enterTouchDelay={300}
                      classes={{
                        tooltip: classes.tooltip,
                      }}
                    >
                      <div>
                        <Button
                          variant="outlined"
                          color={
                            this.props.currentAcqType === "CSI"
                              ? "primary"
                              : "secondary"
                          }
                          className={classes.acquireBtn}
                          onClick={() => this.handleAcquire()}
                          disabled={this.props.acquireButtonDisable}
                        >
                          <TargetIcon className={classes.acqIcon} />
                        </Button>
                      </div>
                    </Tooltip>
                  )}
                </Grid>

                {/* SECTION THREE */}
                <Grid container item className={classes.gridSectionThree}>
                  <Typography
                    component="div"
                    className={classes.AutoAdvanceToggleDiv}
                  >
                    Auto-Advance
                    <Grid
                      component="label"
                      container
                      alignItems="center"
                      spacing={1}
                    >
                      <Grid item className={classes.AutoAdvanceToggleOffLabel}>
                        Off
                      </Grid>
                      <Grid item>
                        <Switch
                          onChange={() => this.props.toggleAutoAdvance()}
                          checked={this.props.autoAdvance}
                          color="primary"
                          disabled={!this.props.device.connectionEstablished}
                        />
                      </Grid>
                      <Grid item className={classes.AutoAdvanceToggleOnLabel}>
                        On
                      </Grid>
                    </Grid>
                  </Typography>
                </Grid>

                {/* SECTION Four */}
                {/* A-LINE */}
                <Grid
                  item
                  container
                  wrap="nowrap"
                  className={classes.gridSectionFour}
                >
                  <Tooltip
                    title="Ping"
                    enterTouchDelay={300}
                    classes={{
                      tooltip: classes.tooltip,
                    }}
                  >
                    <div>
                      <IconButton
                        onClick={() => this.props.fetchPing()}
                        className={classes.pingBtn}
                        disabled={
                          !this.props.device.connectionEstablished ||
                          this.props.acquiring
                        }
                      >
                        <GraphicEqIcon />
                      </IconButton>
                    </div>
                  </Tooltip>

                  <AnimatePresence>
                    {Object.keys(this.props.aLineData).length !== 0 && (
                      <motion.div
                        initial={{ maxWidth: 0 }}
                        animate={{
                          maxWidth: "100%",
                        }}
                        exit={{ maxWidth: 0 }}
                      >
                        <CalibrationPlot />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Grid>
              </Grid>
            </motion.div>
          )}
        </AnimatePresence>
        <AutoAdvanceDialog
          open={this.props.showAutoAdvancePopup || false}
          resetAutoAdvanceTimeout={() => this.resetAutoAdvanceTimeout()}
          handleAutoAdvanceTimeout={() => this.handleAutoAdvanceTimeout()}
          handleAutoAdvanceDismiss={() => this.handleAutoAdvanceDismiss()}
          handleAdvance={() => this.handleAdvance()}
          seconds={this.state.seconds}
          totalSeconds={this.totalSeconds}
        />

        <DiagnosticsDialog open={this.props.diagnosticsDialog} />

        {/* Auto-Advance Acq Type Snackbar */}
        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          onEnter={() =>
            this.props.changeAcqType(this.props.nextAutoStep.acqType)
          }
          onClose={() => this.props.setAutoAdvanceSnackbarState(false)}
          TransitionComponent={Slide}
          open={this.props.showAutoAdvanceSnackbar}
          autoHideDuration={1500} // 1.5 second duration
          className={classes.snackbar}
          message={
            <Typography className={classes.snackbarMessage}>
              Advancing to next acquisition type:{" "}
              {this.props.nextAutoStepType
                ? this.props.acqTypes[this.props.nextAutoStepType]["name"]
                : ""}
            </Typography>
          }
        />

        {this.props.cameraModalStatus && <CameraModal />}
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      fetchPing,
      receivePing,
      createSample,
      fetchConnectDevice,
      receiveAcquisitionComplete,
      requestButtonPress,
      receiveSampleData,
      errorResponse,
      initiateAutoAdvance,
      deviceConnected,
      changeAcqType,
      requestPicture,
      receiveHealth,
      removeSample,
      toggleAutoAdvance,
      setAutoAdvaceDialogState,
      selectLocation,
      setCurrentAcqType,
      setSampleDataAvailable,
      setSampleDataSaved,
      updateSamplesSavedFlag,
      fetchCloseDevice,
      setAutoAdvanceSnackbarState,
      toggleDiagnosticsDialog,
      fetchUploadRawSampleData,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  let dialogFlag =
    state.utility.dialog.studyLoadDialog ||
    state.utility.dialog.studySaveDialog ||
    state.utility.dialog.studyLockDialog ||
    state.utility.dialog.saveProtocolDialog ||
    state.utility.dialog.exportPdfDialog ||
    state.utility.dialog.exportCsvDialog ||
    state.utility.dialog.quitDialog ||
    state.utility.notesFocus
      ? true
      : false;

  let acquireButtonDisable =
    !state.device.connectionEstablished ||
    state.device.acquiring ||
    dialogFlag ||
    state.locations.lastSetLocation.location !==
      state.locations.selectedLocationId ||
    state.locations.lastSetLocation.acqType !== state.locations.currentAcqType;
  return {
    lastSetLocation: state.locations.lastSetLocation,
    currentAcqType: state.locations.currentAcqType,
    acqTypes: state.protocol.acq_types,
    focusElement: state.utility.focusElement,
    dialogFlag: dialogFlag,
    acquiring: state.device.acquiring,
    showAcquireBar: state.utility.showAcquireBar,
    cameraModalStatus: state.utility.cameraModal.status,
    device: state.device,
    allSampleIds: state.samples.allIds,
    showAutoAdvancePopup: state.utility.showAutoAdvancePopup,
    nextAutoStep: state.locations.nextAutoStep,
    aLineData: state.device.aLineData,
    acquireButtonDisable: acquireButtonDisable,
    theme: state.utility.theme,
    autoAdvance: state.utility.autoAdvance,
    showAutoAdvanceSnackbar: state.utility.showAutoAdvanceSnackbar,
    diagnosticsDialog: state.utility.dialog.diagnosticsDialog,
    nextAutoStepType: state.locations.nextAutoStep.acqType,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(AcquireBar));
